import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import HeroFishSchool from '@/components/sections/HeroFishSchool'
import HeroMagneticButton from '@/components/sections/HeroMagneticButton'
import { Button } from '@/components/ui/button'

const easeOut = [0.22, 1, 0.36, 1] as const

const lineReveal = {
  hidden: { y: '115%' },
  visible: (delay: number) => ({
    y: '0%',
    transition: { duration: 0.95, ease: easeOut, delay },
  }),
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: easeOut },
  }),
}

export default function NotFound() {
  useEffect(() => {
    document.title = 'Page Not Found — Gigafish'
  }, [])

  return (
    <div className="min-h-screen bg-accent font-sans text-foreground selection:bg-white selection:text-accent">
      <Navbar />
      <main>
        <section className="relative min-h-[100dvh] w-full overflow-hidden bg-accent flex flex-col justify-center items-center">
          <HeroFishSchool />

          <div className="container relative z-30 flex flex-col justify-center items-center text-center py-32 md:py-40">
            <div className="max-w-5xl mx-auto w-full">
              <h1 className="text-6xl sm:text-7xl md:text-[7.5rem] xl:text-[9rem] font-bold font-heading text-white tracking-tighter leading-[0.88] mb-6 md:mb-8 uppercase">
                <span className="block overflow-hidden pb-[0.06em]">
                  <motion.span
                    className="block"
                    custom={0.08}
                    variants={lineReveal}
                    initial="hidden"
                    animate="visible"
                  >
                    Page
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-[0.06em]">
                  <motion.span
                    className="block"
                    custom={0.22}
                    variants={lineReveal}
                    initial="hidden"
                    animate="visible"
                  >
                    Not Found
                  </motion.span>
                </span>
              </h1>

              <motion.p
                className="text-lg md:text-xl text-slate-200/95 max-w-2xl mx-auto mb-10 md:mb-12 font-light leading-relaxed"
                custom={0.55}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >
                This page swam off into the deep.
                {' '}
                <span className="font-semibold text-white">Let&apos;s get you back to shore.</span>
              </motion.p>

              <motion.div
                className="flex flex-col items-center justify-center gap-4"
                custom={0.75}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >
                <HeroMagneticButton>
                  <Link to="/">
                    <Button
                      size="lg"
                      className="relative px-10 py-8 text-xl max-[400px]:h-auto max-[400px]:px-6 max-[400px]:py-4 max-[400px]:text-base max-[400px]:tracking-wide rounded-full font-bold uppercase tracking-wider border-0 overflow-hidden transition-shadow duration-500
                        bg-gradient-to-b from-white via-slate-50 to-slate-100 text-accent
                        shadow-[0_0_40px_-10px_rgba(255,255,255,0.4),inset_0_1px_0_rgba(255,255,255,0.9)]
                        hover:shadow-[0_0_56px_-8px_rgba(255,255,255,0.55)]
                        hover:from-white hover:via-white hover:to-slate-50
                        before:absolute before:inset-0 before:rounded-full before:content-[''] before:bg-gradient-to-br before:from-white/70 before:via-transparent before:to-transparent before:opacity-90 before:pointer-events-none"
                    >
                      Back to home
                      <ArrowRight className="ml-2 h-6 w-6 max-[400px]:ml-1.5 max-[400px]:h-5 max-[400px]:w-5" />
                    </Button>
                  </Link>
                </HeroMagneticButton>
                <Link
                  to="/new-project"
                  className="inline-flex items-center justify-center px-6 py-2 text-sm md:text-base font-heading font-bold uppercase tracking-wider text-white/80 hover:text-white focus:text-white transition-colors"
                >
                  Start a project
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
